import * as i0 from '@angular/core';
import { InjectionToken, inject, ElementRef, Injectable, booleanAttribute, Directive, Input, HostListener, PLATFORM_ID, NgModule, Optional, SkipSelf, ENVIRONMENT_INITIALIZER } from '@angular/core';
import { ɵINTERNAL_MATOMO_CONFIGURATION as _INTERNAL_MATOMO_CONFIGURATION, ɵInternalMatomoTracker as _InternalMatomoTracker, ɵASYNC_INTERNAL_MATOMO_CONFIGURATION as _ASYNC_INTERNAL_MATOMO_CONFIGURATION, ɵScriptInjector as _ScriptInjector, MatomoTracker, ɵrunOnce as _runOnce, ɵisAutoConfigurationMode as _isAutoConfigurationMode, ɵisExplicitTrackerConfiguration as _isExplicitTrackerConfiguration, ɵgetTrackersConfiguration as _getTrackersConfiguration, ɵappendTrailingSlash as _appendTrailingSlash, ɵcreateMatomoFeature as _createMatomoFeature } from 'ngx-matomo-client/core';
import { isPlatformBrowser } from '@angular/common';
import { debounceTime, identity } from 'rxjs';
const MATOMO_FORM_ANALYTICS_CONFIGURATION = new InjectionToken('MATOMO_FORM_ANALYTICS_CONFIGURATION');
const DEFAULT_FORM_ANALYTICS_CONFIGURATION = {
  disabled: false,
  loadScript: false,
  autoScan: true,
  autoScanDelay: 0
};
const INTERNAL_MATOMO_FORM_ANALYTICS_CONFIGURATION = new InjectionToken('INTERNAL_MATOMO_FORM_ANALYTICS_CONFIGURATION', {
  factory() {
    const matomoConfig = inject(_INTERNAL_MATOMO_CONFIGURATION);
    const userConfig = inject(MATOMO_FORM_ANALYTICS_CONFIGURATION, {
      optional: true
    }) || {};
    const merged = {
      ...DEFAULT_FORM_ANALYTICS_CONFIGURATION,
      ...userConfig
    };
    return {
      ...merged,
      disabled: merged.disabled || matomoConfig.disabled
    };
  }
});
function throwFormNotFoundError(selector) {
  throw new Error(`Directive ${selector} can only be used inside [matomoTrackForm]`);
}
function coerceElement(elementOrRef) {
  return elementOrRef instanceof ElementRef ? elementOrRef.nativeElement : elementOrRef;
}
let MatomoFormAnalytics = /*#__PURE__*/(() => {
  class MatomoFormAnalytics {
    constructor() {
      this.delegate = inject(_InternalMatomoTracker);
    }
    disableFormAnalytics() {
      this.delegate.push(['FormAnalytics::disableFormAnalytics']);
    }
    enableFormAnalytics() {
      this.delegate.push(['FormAnalytics::enableFormAnalytics']);
    }
    enableDebugMode() {
      this.delegate.push(['FormAnalytics::enableDebugMode']);
    }
    setTrackingTimer(delayInMilliSeconds) {
      this.delegate.push(['FormAnalytics::setTrackingTimer', delayInMilliSeconds]);
    }
    scanForForms(root) {
      this.delegate.push(['FormAnalytics::scanForForms', coerceElement(root)]);
    }
    trackForm(formElement) {
      this.delegate.push(['FormAnalytics::trackForm', coerceElement(formElement)]);
    }
    trackFormSubmit(formElement) {
      this.delegate.push(['FormAnalytics::trackFormSubmit', coerceElement(formElement)]);
    }
    trackFormConversion(formElementOrName, formId) {
      this.delegate.push(['FormAnalytics::trackFormConversion', typeof formElementOrName === 'string' ? formElementOrName : coerceElement(formElementOrName), formId]);
    }
    isFormAnalyticsEnabled() {
      return this.delegate.get('FormAnalytics::isFormAnalyticsEnabled');
    }
    static {
      this.ɵfac = function MatomoFormAnalytics_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MatomoFormAnalytics)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MatomoFormAnalytics,
        factory: MatomoFormAnalytics.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MatomoFormAnalytics;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TrackFormDirective = /*#__PURE__*/(() => {
  class TrackFormDirective {
    set matomoIgnore(ignore) {
      if (ignore) {
        this.elementRef.nativeElement.setAttribute('data-matomo-ignore', '');
      } else {
        this.elementRef.nativeElement.removeAttribute('data-matomo-ignore');
      }
    }
    set matomoTrackForm(name) {
      if (name) {
        this.elementRef.nativeElement.setAttribute('data-matomo-name', name);
      } else {
        this.elementRef.nativeElement.removeAttribute('data-matomo-name');
      }
      if (this.initialized) {
        this.track();
      }
    }
    constructor() {
      this.elementRef = inject(ElementRef);
      this.tracker = inject(MatomoFormAnalytics);
      this.initialized = false;
      /** If true, will track a conversion after form submit */
      this.trackConversionOnSubmit = false;
      this.elementRef.nativeElement.setAttribute('data-matomo-form', '');
    }
    ngAfterViewInit() {
      this.track();
      this.initialized = true;
    }
    track() {
      this.tracker.trackForm(this.elementRef);
    }
    trackSubmit() {
      this.tracker.trackFormSubmit(this.elementRef);
    }
    trackConversion() {
      this.tracker.trackFormConversion(this.elementRef);
    }
    trackFormConversionOnSubmit() {
      if (this.trackConversionOnSubmit) {
        this.trackConversion();
      }
    }
    static {
      this.ɵfac = function TrackFormDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TrackFormDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TrackFormDirective,
        selectors: [["", "matomoTrackForm", ""]],
        hostBindings: function TrackFormDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("submit", function TrackFormDirective_submit_HostBindingHandler() {
              return ctx.trackFormConversionOnSubmit();
            });
          }
        },
        inputs: {
          trackConversionOnSubmit: [2, "trackConversionOnSubmit", "trackConversionOnSubmit", booleanAttribute],
          matomoIgnore: [2, "matomoIgnore", "matomoIgnore", booleanAttribute],
          matomoTrackForm: "matomoTrackForm"
        },
        exportAs: ["matomoTrackForm"]
      });
    }
  }
  return TrackFormDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TrackFormFieldDirective = /*#__PURE__*/(() => {
  class TrackFormFieldDirective {
    constructor() {
      this.elementRef = inject(ElementRef);
      this.form = inject(TrackFormDirective, {
        optional: true
      }) ?? throwFormNotFoundError('[matomoTrackFormField]');
      this.initialized = false;
    }
    set matomoIgnore(ignore) {
      if (ignore) {
        this.elementRef.nativeElement.setAttribute('data-matomo-ignore', '');
      } else {
        this.elementRef.nativeElement.removeAttribute('data-matomo-ignore');
      }
    }
    set matomoTrackFormField(name) {
      if (name) {
        this.elementRef.nativeElement.setAttribute('data-matomo-name', name);
      } else {
        this.elementRef.nativeElement.removeAttribute('data-matomo-name');
      }
      if (this.initialized) {
        this.track();
      }
    }
    ngAfterViewInit() {
      this.track();
      this.initialized = true;
    }
    track() {
      this.form.track();
    }
    static {
      this.ɵfac = function TrackFormFieldDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TrackFormFieldDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TrackFormFieldDirective,
        selectors: [["", "matomoTrackFormField", ""]],
        inputs: {
          matomoIgnore: [2, "matomoIgnore", "matomoIgnore", booleanAttribute],
          matomoTrackFormField: "matomoTrackFormField"
        },
        exportAs: ["matomoTrackFormField"]
      });
    }
  }
  return TrackFormFieldDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TrackFormsDirective = /*#__PURE__*/(() => {
  class TrackFormsDirective {
    constructor() {
      this.elementRef = inject(ElementRef);
      this.tracker = inject(MatomoFormAnalytics);
    }
    ngAfterViewInit() {
      this.scan();
    }
    scan() {
      this.tracker.scanForForms(this.elementRef);
    }
    trackSubmit(element) {
      this.tracker.trackFormSubmit(element);
    }
    trackConversion(element) {
      this.tracker.trackFormConversion(element);
    }
    static {
      this.ɵfac = function TrackFormsDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TrackFormsDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TrackFormsDirective,
        selectors: [["", "matomoTrackForms", ""]],
        exportAs: ["matomoTrackForms"]
      });
    }
  }
  return TrackFormsDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const DEFAULT_SCRIPT_SUFFIX = 'plugins/FormAnalytics/tracker.min.js';
let MatomoFormAnalyticsInitializer = /*#__PURE__*/(() => {
  class MatomoFormAnalyticsInitializer {
    constructor() {
      this.config = inject(INTERNAL_MATOMO_FORM_ANALYTICS_CONFIGURATION);
      this.coreConfig = inject(_ASYNC_INTERNAL_MATOMO_CONFIGURATION);
      this.scriptInjector = inject(_ScriptInjector);
      this.tracker = inject(MatomoTracker);
      this.formAnalytics = inject(MatomoFormAnalytics);
      this.platformId = inject(PLATFORM_ID);
      this.initialize = _runOnce(async () => {
        // Do not set-up router if running on server
        if (!isPlatformBrowser(this.platformId)) {
          return;
        }
        if (this.config.disabled) {
          this.formAnalytics.disableFormAnalytics();
          return;
        }
        if (this.config.loadScript) {
          const scriptUrl = typeof this.config.loadScript === 'boolean' ? await this.buildDefaultScriptUrl() : this.config.loadScript;
          this.scriptInjector.injectDOMScript(scriptUrl);
        }
        if (this.config.autoScan) {
          const delayOp = this.config.autoScanDelay ? debounceTime(this.config.autoScanDelay) : identity;
          this.pageTrackedSubscription = this.tracker.pageViewTracked.pipe(delayOp).subscribe(() => this.formAnalytics.scanForForms());
        }
      });
    }
    ngOnDestroy() {
      this.pageTrackedSubscription?.unsubscribe();
    }
    async buildDefaultScriptUrl() {
      const config = await this.coreConfig;
      if (_isAutoConfigurationMode(config) && _isExplicitTrackerConfiguration(config)) {
        const [mainTracker] = _getTrackersConfiguration(config);
        if (mainTracker) {
          return _appendTrailingSlash(mainTracker.trackerUrl) + DEFAULT_SCRIPT_SUFFIX;
        }
      }
      throw new Error('Cannot resolve default matomo FormAnalytics plugin script url. ' + 'Please explicitly provide `loadScript` configuration property instead of `true`');
    }
    static {
      this.ɵfac = function MatomoFormAnalyticsInitializer_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MatomoFormAnalyticsInitializer)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MatomoFormAnalyticsInitializer,
        factory: MatomoFormAnalyticsInitializer.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MatomoFormAnalyticsInitializer;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const MATOMO_FORM_ANALYTICS_DIRECTIVES = [TrackFormDirective, TrackFormsDirective, TrackFormFieldDirective];
let MatomoFormAnalyticsModule = /*#__PURE__*/(() => {
  class MatomoFormAnalyticsModule {
    constructor(formAnalytics, parent) {
      this.formAnalytics = formAnalytics;
      if (!parent) {
        // Do not initialize if it is already (by a parent module)
        this.formAnalytics.initialize();
      }
    }
    static forRoot(config = {}) {
      return {
        ngModule: MatomoFormAnalyticsModule,
        providers: [{
          provide: MATOMO_FORM_ANALYTICS_CONFIGURATION,
          useValue: config
        }]
      };
    }
    static {
      this.ɵfac = function MatomoFormAnalyticsModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MatomoFormAnalyticsModule)(i0.ɵɵinject(MatomoFormAnalyticsInitializer), i0.ɵɵinject(MatomoFormAnalyticsModule, 12));
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: MatomoFormAnalyticsModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return MatomoFormAnalyticsModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/** Enable automatic page views tracking */
function withFormAnalytics(config) {
  const providers = [{
    provide: MATOMO_FORM_ANALYTICS_CONFIGURATION,
    useValue: config
  }, {
    provide: ENVIRONMENT_INITIALIZER,
    multi: true,
    useValue() {
      inject(MatomoFormAnalyticsInitializer).initialize();
    }
  }];
  return _createMatomoFeature("FormAnalytics" /* FormAnalyticsMatomoFeatureKind.FormAnalytics */, providers);
}

/*
 * Public API Surface of plugin form-analytics
 */

/**
 * Generated bundle index. Do not edit.
 */

export { MATOMO_FORM_ANALYTICS_CONFIGURATION, MATOMO_FORM_ANALYTICS_DIRECTIVES, MatomoFormAnalytics, MatomoFormAnalyticsModule, TrackFormDirective, TrackFormFieldDirective, TrackFormsDirective, withFormAnalytics };
